// Curated compliance resources surfaced to sellers during attestation.
//
// IMPORTANT: Stoop is not the authoritative source on any of these laws.
// Each link points at the actual government source (or other primary
// authority). When linked pages change or move, update here — but if a
// regulation itself changes, the seller's responsibility to comply doesn't
// change because we updated this file. The attestation flow makes that
// crystal clear in the acknowledgment text.
//
// Add states as the platform expands. Default fallback at the bottom of
// `getComplianceResources()` returns generic federal-level links plus a
// "we haven't curated state-specific resources yet — please research your
// state's cottage food and sales tax rules" advisory message.
//
// Bump CURRENT_ATTESTATION_VERSION whenever the acknowledgment text or the
// required-resources list changes. Existing sellers will be required to
// re-attest at the new version before publishing further listings.

export type SellerComplianceCategory =
  | "produce"
  | "baked_goods"
  | "prepared_foods"
  | "dairy_eggs"
  | "meat_poultry"
  | "non_food";

export interface ComplianceResource {
  title: string;
  description: string;
  url: string;
  // Categories this resource applies to. Used to filter the displayed list
  // based on what the seller said they'd sell. If empty, applies to all.
  applicableCategories: SellerComplianceCategory[];
}

export interface ComplianceCategoryOption {
  value: SellerComplianceCategory;
  label: string;
  examples: string;
}

export const CURRENT_ATTESTATION_VERSION = "v1.2";

export const SELLER_COMPLIANCE_CATEGORIES: ComplianceCategoryOption[] = [
  {
    value: "produce",
    label: "Fresh produce",
    examples: "vegetables, fruits, herbs, flowers",
  },
  {
    value: "baked_goods",
    label: "Baked goods",
    examples: "breads, cookies, cakes, pastries",
  },
  {
    value: "prepared_foods",
    label: "Prepared / preserved foods",
    examples: "jams, jellies, sauces, ferments, dry mixes",
  },
  {
    value: "dairy_eggs",
    label: "Dairy & eggs",
    examples: "eggs, milk, cheese, yogurt",
  },
  {
    value: "meat_poultry",
    label: "Meat & poultry",
    examples: "raw or processed meats, poultry, fish",
  },
  {
    value: "non_food",
    label: "Non-food items",
    examples: "soaps, candles, crafts, plants/seedlings",
  },
];

// Every URL below verified live (HTTP 200) on 2026-05-06. When updating
// this list, re-curl each URL to confirm — slugs on agriculture.ny.gov
// have changed at least three times in the past year.
const NEW_YORK_RESOURCES: ComplianceResource[] = [
  {
    title: "NY Home Processing (Article 20-C exemption)",
    description:
      "Lets you sell certain non-hazardous baked goods, jams, and dry mixes from a home kitchen without a commercial kitchen license. Has limits on what foods qualify and where you can sell.",
    url: "https://agriculture.ny.gov/food-safety/home-processing",
    applicableCategories: ["baked_goods", "prepared_foods"],
  },
  {
    title: "NY Sanitary Regulations for Farmers Markets — Plain-English Guidance (PDF)",
    description:
      "The most useful starting point: NY State's plain-English walkthrough of the sanitary regs that apply to direct-to-consumer food sales. Read this first — it explains what the formal regs (next link) actually mean for a small seller.",
    url: "https://agriculture.ny.gov/system/files/documents/2020/04/guidanceonsanitaryregulationsfornysfarmersmarkets.pdf",
    applicableCategories: ["produce", "baked_goods", "prepared_foods", "dairy_eggs", "meat_poultry"],
  },
  {
    title: "NY Sanitary Regulations for Farmers Markets — Formal Regs (PDF)",
    description:
      "The formal NY State regs covering temperature control, sampling, labeling, and direct-to-consumer sales. Companion to the guidance doc above.",
    url: "https://agriculture.ny.gov/system/files/documents/2020/03/sanitaryregulationsforfarmersmarkets_0.pdf",
    applicableCategories: ["produce", "baked_goods", "prepared_foods", "dairy_eggs", "meat_poultry"],
  },
  {
    title: "NY Food Safety at Farmers Markets",
    description:
      "Plain-English food safety guidance specific to direct-to-consumer farmers market sales. Covers what you need to do regardless of category.",
    url: "https://agriculture.ny.gov/food-safety/food-safety-farmers-markets",
    applicableCategories: ["produce", "baked_goods", "prepared_foods", "dairy_eggs", "meat_poultry"],
  },
  {
    title: "NY Food Business Licensing",
    description:
      "When you need a food processor license vs. when an exemption (like Article 20-C) applies. Start here if you're unsure whether your operation needs licensing.",
    url: "https://agriculture.ny.gov/food-business-licensing",
    applicableCategories: ["baked_goods", "prepared_foods", "dairy_eggs", "meat_poultry"],
  },
  {
    title: "NY Livestock & Poultry",
    description:
      "Animal product inspection rules and slaughter requirements. Poultry has a limited on-farm exemption (under 1,000 birds/year); red meat (pork, beef, lamb, goat) has none and must be USDA-inspected to sell as cuts. Egg sales and meat both fall under this division.",
    url: "https://agriculture.ny.gov/animals/livestock-poultry",
    applicableCategories: ["meat_poultry", "dairy_eggs"],
  },
  {
    title: "NY Milk & Dairy",
    description:
      "Raw milk permits, cheese processor licensing, and frozen dessert rules. Dairy is heavily regulated in NY — check here before listing anything dairy-based.",
    url: "https://agriculture.ny.gov/milk-dairy",
    applicableCategories: ["dairy_eggs"],
  },
  {
    title: "NY Farm Products & Services",
    description:
      "Programs and resources for direct-from-farm sales, including farm stands and producer-to-consumer marketing.",
    url: "https://agriculture.ny.gov/food-safety/farm-products-services",
    applicableCategories: ["produce", "dairy_eggs", "meat_poultry"],
  },
  {
    title: "NY Sales Tax — Vendor Registration",
    description:
      "Most food for home consumption is sales-tax exempt in NY, but some prepared foods and non-food items require sales tax collection. Register before your first taxable sale.",
    url: "https://www.tax.ny.gov/bus/st/register.htm",
    applicableCategories: ["prepared_foods", "non_food"],
  },
  {
    title: "FDA Cosmetics — Soap & Skin Claims",
    description:
      "Soaps that make skin claims (moisturizing, antibacterial) are regulated as cosmetics under federal law. True soap with no skin claims is exempt but still has labeling requirements.",
    url: "https://www.fda.gov/cosmetics",
    applicableCategories: ["non_food"],
  },
];

const FEDERAL_FALLBACK_RESOURCES: ComplianceResource[] = [
  {
    title: "IRS Self-Employment Tax — Schedule C",
    description:
      "Selling on Stoop is self-employment income. Even without an LLC, report earnings on Schedule C of your federal return. Stripe issues a 1099-K to you above $5,000/year.",
    url: "https://www.irs.gov/businesses/small-businesses-self-employed/self-employed-individuals-tax-center",
    applicableCategories: [],
  },
];

export function getComplianceResources(
  state: string,
  categories: SellerComplianceCategory[],
): { resources: ComplianceResource[]; stateRecognized: boolean; advisoryMessage?: string } {
  const normalizedState = String(state || "").trim().toUpperCase();
  const requestedCategories = new Set(categories);

  const filterByCategories = (list: ComplianceResource[]): ComplianceResource[] =>
    list.filter(
      (r) =>
        r.applicableCategories.length === 0 ||
        r.applicableCategories.some((c) => requestedCategories.has(c)),
    );

  if (normalizedState === "NY" || normalizedState === "NEW YORK") {
    return {
      resources: [
        ...filterByCategories(NEW_YORK_RESOURCES),
        ...FEDERAL_FALLBACK_RESOURCES,
      ],
      stateRecognized: true,
    };
  }

  return {
    resources: FEDERAL_FALLBACK_RESOURCES,
    stateRecognized: false,
    advisoryMessage:
      "We haven't curated state-specific compliance resources for your state yet. " +
      "Before listing, please research your state's cottage food laws, food " +
      "processor licensing requirements, and sales tax registration through " +
      "your state's Department of Agriculture and Department of Tax & Finance. " +
      "If you'd like Stoop to add curated resources for your state, contact support.",
  };
}
